<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  title: { type: String, required: true },
  ariaLabel: { type: String, default: '' },
  items: { type: Array, default: () => [] },
  sections: { type: Array, default: () => [] },
  showNav: { type: Boolean, default: true },
});

const route = useRoute();
const router = useRouter();

const hasSections = computed(() => props.sections && props.sections.length > 0);

const flushSettingsContent = computed(() =>
  route.matched.some(r => r.meta.flushSettingsContent === true)
);

const isActive = item => {
  if (item.activeOn?.includes(route.name)) return true;
  if (item.activeOnRoutes?.includes(route.name)) return true;
  if (item.name && route.name === item.name) return true;
  if (item.to) {
    const resolved = router.resolve(item.to);
    if (resolved?.path && route.path === resolved.path) return true;
  }
  return false;
};

const resolveHref = item => {
  return item?.to ? router.resolve(item.to).href : undefined;
};

const isModifiedEvent = event => {
  return (
    event?.metaKey ||
    event?.ctrlKey ||
    event?.shiftKey ||
    event?.altKey ||
    event?.button === 1
  );
};

const onNavigate = (event, item) => {
  if (!item?.to || isModifiedEvent(event)) return;

  event.preventDefault();
  router.push(item.to);
};
</script>

<template>
  <section
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-n-surface-1 md:flex-row"
  >
    <aside
      v-if="showNav"
      class="hidden h-full w-[216px] shrink-0 border-r border-n-weak bg-n-surface-1 md:flex md:flex-col"
      :aria-label="ariaLabel || title"
    >
      <div class="flex h-12 shrink-0 items-start px-3 pt-4">
        <h2
          class="min-w-0 flex-1 truncate text-lg font-semibold text-n-slate-12"
        >
          {{ title }}
        </h2>
      </div>
      <nav class="flex-1 overflow-y-auto px-[11px] pb-4 pt-4">
        <template v-if="hasSections">
          <div v-for="section in sections" :key="section.name" class="mb-2">
            <div
              v-if="section.label"
              class="mb-1.5 mt-1 flex h-9 w-full items-center px-2 text-xs font-semibold text-n-slate-11"
            >
              {{ section.label }}
            </div>
            <a
              v-for="leaf in section.children"
              :key="leaf.name || leaf.label"
              :href="resolveHref(leaf)"
              class="mb-1 flex h-[33px] w-full items-center gap-2 rounded-md px-2 text-sm transition-colors text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
              :class="[
                isActive(leaf)
                  ? 'bg-n-alpha-2 font-semibold text-n-slate-12 hover:text-n-slate-12'
                  : '',
              ]"
              @click="onNavigate($event, leaf)"
            >
              <Icon
                v-if="leaf.icon"
                :icon="leaf.icon"
                class="size-5 shrink-0"
                :class="isActive(leaf) ? 'text-n-slate-12' : 'text-n-slate-11'"
              />
              <span class="min-w-0 flex-1 truncate text-start">{{
                leaf.label
              }}</span>
            </a>
          </div>
        </template>
        <template v-else>
          <a
            v-for="item in items"
            :key="item.name || item.label"
            :href="resolveHref(item)"
            class="mb-1 flex h-[33px] w-full items-center gap-2 rounded-md px-2 text-sm transition-colors text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :class="[
              isActive(item)
                ? 'bg-n-alpha-2 font-semibold text-n-slate-12 hover:text-n-slate-12'
                : '',
            ]"
            @click="onNavigate($event, item)"
          >
            <Icon
              v-if="item.icon"
              :icon="item.icon"
              class="size-5 shrink-0"
              :class="isActive(item) ? 'text-n-slate-12' : 'text-n-slate-11'"
            />
            <span class="min-w-0 flex-1 truncate text-start">{{
              item.label
            }}</span>
          </a>
        </template>
      </nav>
    </aside>

    <div
      class="flex min-h-0 min-w-0 flex-1 flex-col border-n-weak"
      :class="[
        showNav && 'md:border-l',
        flushSettingsContent
          ? 'overflow-hidden p-0'
          : 'overflow-auto px-0 pb-12 pt-3',
      ]"
    >
      <div class="flex w-full min-w-0 max-w-none flex-1 flex-col items-stretch">
        <slot />
      </div>
    </div>
  </section>
</template>
